import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { RootState } from '../store/store';
import { poolMemberPaymentsService, PoolMemberPayment } from '../services/poolMemberPaymentsService';
import { poolMembersService, PoolMember } from '../services/poolMembersService';
import { transfersService } from '../services/transfersService';
import { Transfer, TransferStatus } from '../types';

const PoolMemberPaymentsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [myMember, setMyMember] = useState<PoolMember | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [payments, setPayments] = useState<PoolMemberPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [me, myTransfers] = await Promise.all([
        poolMembersService.getMe(),
        transfersService.getAll({ limit: '200' }),
      ]);

      setMyMember(me);
      setTransfers(myTransfers.data);

      const myPayments = await poolMemberPaymentsService.getAll({ poolMemberId: me.id });
      setPayments(myPayments);
    } catch {
      toast.error('Ödemeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getPaidForTransfer = (transferId: string) =>
    payments.filter(p => p.transferId === transferId).reduce((sum, p) => sum + Number(p.amount), 0);

  const totalEarned = transfers.reduce((sum, t) => sum + Number(t.providerPrice || 0), 0);
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
  const totalPending = totalEarned - totalPaid;

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500">Yükleniyor...</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ödemelerim</h1>
        <p className="text-sm text-gray-500 mt-1">Aldığınız transferler ve ödeme durumları</p>
      </div>

      {/* Özet */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 text-center">
          <p className="text-xs text-gray-500 mb-1">Toplam Kazanç</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{totalEarned.toFixed(0)} TL</p>
          <p className="text-xs text-gray-400 mt-0.5">{transfers.length} transfer</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800 text-center">
          <p className="text-xs text-green-600 mb-1">Ödenen</p>
          <p className="text-xl font-bold text-green-700 dark:text-green-400">{totalPaid.toFixed(0)} TL</p>
          <p className="text-xs text-green-500 mt-0.5">{payments.length} ödeme</p>
        </div>
        <div className={`rounded-xl p-4 border text-center ${totalPending > 0 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}>
          <p className={`text-xs mb-1 ${totalPending > 0 ? 'text-orange-600' : 'text-gray-500'}`}>Bekleyen</p>
          <p className={`text-xl font-bold ${totalPending > 0 ? 'text-orange-700 dark:text-orange-400' : 'text-gray-400'}`}>{totalPending.toFixed(0)} TL</p>
          <p className={`text-xs mt-0.5 ${totalPending > 0 ? 'text-orange-400' : 'text-gray-400'}`}>alacak</p>
        </div>
      </div>

      {/* Transfer Listesi */}
      {transfers.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <p className="text-4xl mb-3">💰</p>
          <p className="text-gray-500">Henüz transfer yok</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(t => {
            const price = Number(t.providerPrice || 0);
            const paid = getPaidForTransfer(t.id);
            const remaining = price - paid;
            const isPaid = price > 0 && remaining <= 0;
            const isPartial = paid > 0 && remaining > 0;

            return (
              <div key={t.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                {/* Üst: tarih + durum */}
                <div className="flex justify-between items-center px-4 pt-3 pb-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {format(new Date(t.transferDate), 'dd.MM.yyyy')}
                    <span className="text-gray-400 font-normal ml-2">{t.transferTime?.substring(0, 5)}</span>
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    t.status === TransferStatus.COMPLETED ? 'bg-green-100 text-green-700' :
                    t.status === TransferStatus.CLAIMED ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {t.status === TransferStatus.COMPLETED ? 'Tamamlandı' :
                     t.status === TransferStatus.CLAIMED ? 'Devam Ediyor' : t.status}
                  </span>
                </div>

                {/* Güzergah */}
                <div className="px-4 pb-2">
                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {t.pickupLocation} → {t.dropoffLocation}
                  </p>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">{t.transferCode}</p>
                </div>

                {/* Alt: ücret bilgileri */}
                <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-xs text-gray-400 mb-0.5">Transfer Ücreti</p>
                    <p className="font-bold text-gray-900 dark:text-white text-sm">
                      {price > 0 ? `${price.toFixed(0)} TL` : '-'}
                    </p>
                  </div>
                  <div className="text-center border-x border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-green-500 mb-0.5">Ödenen</p>
                    <p className={`font-bold text-sm ${paid > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {paid > 0 ? `${paid.toFixed(0)} TL` : '0 TL'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className={`text-xs mb-0.5 ${remaining > 0 ? 'text-orange-500' : 'text-gray-400'}`}>Kalan</p>
                    {isPaid ? (
                      <p className="font-bold text-sm text-green-500">✓ Ödendi</p>
                    ) : (
                      <p className={`font-bold text-sm ${remaining > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                        {remaining > 0 ? `${remaining.toFixed(0)} TL` : '0 TL'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Kısmi ödeme varsa göster */}
                {isPartial && (
                  <div className="px-4 pb-3">
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-green-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (paid / price) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1 text-right">{((paid / price) * 100).toFixed(0)}% ödendi</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PoolMemberPaymentsPage;
